import React, { FC, useEffect, useState } from 'react';
import { Product } from '@commercetools/frontend-domain-types/product/Product';
import { SwiperOptions } from 'swiper';
import { Subtitle, SubtitleProps } from '@loomstateorg/shared-frontend';
import { DEFAULT_CARD_TYPE, ProductCardType, tileRegistry } from '@loomstateorg/shared-frontend/components/commercetools-ui/organisms/product/product-tile/registry';
import type { ProductTileLookProps } from '@loomstateorg/shared-frontend/components/commercetools-ui/organisms/product/product-tile';
import { SustainabilityItem } from 'components/commercetools-ui/types';
import Wrapper from 'components/HOC/wrapper';
import { mediumDesktop, tablet } from '@loomstateorg/shared-frontend/utils/screensizes';
import {
  Slider,
  Typography,
  loadProductConfig,
  loadProductConfigFromStorage,
  loadShowSecondImageOnHoverFromStorage,
} from '@loomstateorg/shared-frontend';
import { Reference } from 'types/reference';
import Link from '@loomstateorg/shared-frontend/components/commercetools-ui/organisms/content/link';
import useTrack from '@loomstateorg/shared-frontend/components/commercetools-ui/organisms/product/product-slider/useTrack';

export interface ProductSliderProps extends Partial<SwiperOptions> {
  breakpoints?: any;
  products: Product[];
  title?: string;
  titleVariant?: 'xs' | 'sm' | 'lg';
  subline?: string;
  subtitleVariant?: SubtitleProps['variant'];
  ctaLabel?: string;
  ctaLink?: Reference;
  clearDefaultWrapperStyles?: boolean;
  slidesPerView?: number;
  disableProductVariants?: boolean;
  innerArrows?: boolean;
  solidArrows?: boolean;
  onProductClick?: (product: Product) => void;
  classNames?: {
    title?: string;
  };
  variant?: 'normal' | 'cart';
  sustainability?: {
    certificationsItem?: SustainabilityItem;
    environmentalItem?: SustainabilityItem;
  };
  dataTestId?: string;
  tileLook?: ProductTileLookProps;
}

const ProductSlider: FC<ProductSliderProps> = ({
  products,
  tileLook,
  title,
  subline,
  ctaLabel,
  ctaLink,
  onProductClick,
  titleVariant = 'lg',
  subtitleVariant = 'lg',
  clearDefaultWrapperStyles = false,
  disableProductVariants = false,
  innerArrows = true,
  solidArrows = true,
  breakpoints = {},
  classNames = {},
  sustainability,
  dataTestId,
  ...props
}) => {
  const { trackClick } = useTrack();

  const [cardType, setCardType] = useState<ProductCardType | null>(null);
  const [showSecondImageOnHover, setShowSecondImageOnHover] = useState(false);
  const TileComponent = cardType ? (tileRegistry[cardType] ?? tileRegistry[DEFAULT_CARD_TYPE]) : null;

  useEffect(() => {
    const cachedCardType = loadProductConfigFromStorage();
    if (cachedCardType) setCardType(cachedCardType);

    const cachedShowSecondImageOnHover = loadShowSecondImageOnHoverFromStorage();
    if (cachedShowSecondImageOnHover !== null) setShowSecondImageOnHover(cachedShowSecondImageOnHover);

    loadProductConfig().then((config) => {
      setCardType(config.cardType);
      setShowSecondImageOnHover(config.showSecondImageOnHover);
    });
  }, []);

  return (
    <div data-testid={dataTestId ? `${dataTestId}-section` : 'product-slider'}>
    <Wrapper clearDefaultStyles={true} className="mt-52">
      <div>
        <Typography
          data-testid={dataTestId ? `${dataTestId}-section-title` : 'product-slider-title'}
          className={`mb-12 uppercase text-24 md:text-36 ${classNames.title ?? ''}`}>
          {title}
        </Typography>
        {(subline || ctaLink) && (
          <div className="mt-8 flex items-center justify-between md:mt-16 lg:mt-14">
            {subline && <Subtitle subtitle={subline} variant={subtitleVariant} data-testid={dataTestId ? `${dataTestId}-section-subtitle` : undefined} />}
            {ctaLink && (
              <div className="hidden lg:block">
                <Link target={ctaLink} withArrow data-testid="view-all">
                  <span className="text-secondary-black font-medium leading-[24px]">{ctaLabel}</span>
                </Link>
              </div>
            )}
          </div>
        )}
        {ctaLink && (
          <div className="mt-20 block md:mt-16 lg:hidden">
            <Link target={ctaLink} withArrow data-testid="view-all">
              <span className="text-secondary-black font-medium leading-[24px]">{ctaLabel}</span>
            </Link>
          </div>
        )}
      </div>
      <div className="mt-20 md:mt-24 lg:mt-20">
        <div className="relative mt-6 w-full">
          <Slider
            containerClassName="product-slider-container"
            slidesPerView={1.2}
            slidesPerGroup={1}
            dots={false}
            arrows
            innerArrows={innerArrows}
            solidArrows={solidArrows}
            nextButtonStyles={{ transform: 'translateY(-150%)' }}
            prevButtonStyles={{ transform: 'translateY(-150%)' }}
            allowTouchMove
            spaceBetween={16}
            breakpoints={{
              [tablet]: {
                slidesPerView: 2.15,
                spaceBetween: 25,
                ...(breakpoints[tablet] ?? {}),
              },
              [mediumDesktop]: {
                slidesPerView: 3,
                slidesPerGroup: 3,
                spaceBetween: 25,
                ...(breakpoints[mediumDesktop] ?? {}),
              },
            }}
            {...props}
          >
            {TileComponent && products.map((product, index) => (
              <TileComponent
                key={product.slug}
                index={index}
                disableVariants={disableProductVariants}
                product={product}
                onClick={() => {
                  trackClick(product, index + 1);
                  onProductClick?.(product);
                }}
                sustainability={sustainability}
                dataTestId={dataTestId ? `${dataTestId}-product` : undefined}
                showSecondImageOnHover={showSecondImageOnHover}
                    {...tileLook}
              />
            ))}
          </Slider>
        </div>
      </div>
    </Wrapper>
    </div>
  );
};

export default ProductSlider;
